import React, { useState, useEffect } from 'react';
import { Form, Input, Radio, Select, Button, Checkbox, Row, Col, Card, Typography, Dropdown, Menu } from 'antd';
import { DownOutlined, CheckOutlined } from '@ant-design/icons';

const { TextArea } = Input;
const { Title } = Typography;
const { Option } = Select;

const FormulaBuilder = ({ form }) => {
  const [formulaText, setFormulaText] = useState('');
  const [calculationType, setCalculationType] = useState('flatAmount');
  const [cursorPosition, setCursorPosition] = useState(0);
  const textAreaRef = React.useRef(null);

  useEffect(() => {
    if (form.getFieldValue('calculationType')) {
      setCalculationType(form.getFieldValue('calculationType'));
    }
  }, [form]);

  const handleCalculationTypeChange = (e) => {
    setCalculationType(e.target.value);
    form.setFieldsValue({ formula: '' });
    setFormulaText('');
  };

  const insertAtCursor = (textToInsert) => {
    const currentFormula = formulaText || '';
    const newFormula = currentFormula.substring(0, cursorPosition) + 
                        textToInsert + 
                        currentFormula.substring(cursorPosition);
    
    setFormulaText(newFormula);
    form.setFieldsValue({ formula: newFormula });
    
    // Set focus back to textarea and update cursor position
    setTimeout(() => {
      if (textAreaRef.current) {
        // Access the input element using the textAreaRef
        const textAreaElement = textAreaRef.current.resizableTextArea.textArea;
        textAreaElement.focus();
        
        // Now we can safely use setSelectionRange on the actual DOM element
        const newPosition = cursorPosition + textToInsert.length;
        textAreaElement.setSelectionRange(newPosition, newPosition);
        setCursorPosition(newPosition);
      }
    }, 0);
  };

  const handleTextAreaChange = (e) => {
    setFormulaText(e.target.value);
    form.setFieldsValue({ formula: e.target.value });
  };

  const handleTextAreaClick = (e) => {
    setCursorPosition(e.target.selectionStart);
  };

  const handleTextAreaKeyUp = (e) => {
    setCursorPosition(e.target.selectionStart);
  };

  const checkSyntax = () => {
    try {
      // This is a simplified validation - in production, use a proper formula parser
      // eslint-disable-next-line no-new-func
      new Function('salary', 'basic', 'amount', 'employee_salary', 'earning_basic_amount', `return ${formulaText}`);
      form.setFields([
        {
          name: 'formula',
          errors: [],
        },
      ]);
      return true;
    } catch (error) {
      form.setFields([
        {
          name: 'formula',
          errors: [`Formula syntax error: ${error.message}`],
        },
      ]);
      return false;
    }
  };

  const componentMenuItems = [
    { key: 'earning_basic_amount', label: 'Basic Salary' },
    { key: 'earning_hra_amount', label: 'HRA' },
    { key: 'earning_da_amount', label: 'DA' },
    { key: 'earning_ta_amount', label: 'TA' },
    { key: 'employee_salary', label: 'Employee Salary' },
  ];

  const functionMenuItems = [
    { key: 'MAX', label: 'MAX' },
    { key: 'MIN', label: 'MIN' },
    { key: 'ROUND', label: 'ROUND' },
    { key: 'FLOOR', label: 'FLOOR' },
    { key: 'CEILING', label: 'CEILING' },
    { key: 'IF', label: 'IF' },
    { key: 'OR', label: 'OR' },
  ];

  const operatorMenuItems = [
    { key: '+', label: '+' },
    { key: '-', label: '-' },
    { key: '*', label: '*' },
    { key: '/', label: '/' },
    { key: '%', label: '%' },
    { key: '>', label: '>' },
    { key: '<', label: '<' },
    { key: '>=', label: '>=' },
    { key: '<=', label: '<=' },
    { key: '==', label: '==' },
    { key: '!=', label: '!=' },
    { key: '&&', label: '&&' },
    { key: '||', label: '||' },
    { key: '(', label: '(' },
    { key: ')', label: ')' },
    { key: ',', label: ',' },
    { key: ':', label: ':' },
    { key: '?', label: '?' },
  ];

  const handleComponentSelect = ({ key }) => {
    insertAtCursor(key);
  };

  const handleFunctionSelect = ({ key }) => {
    let textToInsert = key;
    if (key === 'IF') {
      textToInsert = 'IF(condition, trueValue, falseValue)';
    } else if (key === 'MAX' || key === 'MIN') {
      textToInsert = `${key}(value1, value2)`;
    } else {
      textToInsert = `${key}(value)`;
    }
    insertAtCursor(textToInsert);
  };

  const handleOperatorSelect = ({ key }) => {
    insertAtCursor(key);
  };

  return (
    <>
      <Form.Item
        name="payType"
        label="Pay Type"
        rules={[{ required: true, message: 'Please select pay type' }]}
      >
        <Radio.Group>
          <Radio value="fixed">Fixed Pay (Fixed amount paid at the end of every month.)</Radio>
          <Radio value="variable">Variable Pay (Variable amount paid during any payroll.)</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item
        name="calculationType"
        label="Calculation Type"
        rules={[{ required: true, message: 'Please select calculation type' }]}
      >
        <Radio.Group onChange={handleCalculationTypeChange}>
          <Radio value="flatAmount">Flat Amount</Radio>
          <Radio value="customFormula">Custom Formula</Radio>
        </Radio.Group>
      </Form.Item>

      {calculationType === 'customFormula' && (
        <>
          <Form.Item label="Formula" required>
            <div style={{ marginBottom: '10px' }}>
              <Button.Group>
                <Dropdown 
                  menu={{
                    items: componentMenuItems,
                    onClick: handleComponentSelect
                  }}
                >
                  <Button>
                    Add Component <DownOutlined />
                  </Button>
                </Dropdown>
                <Dropdown 
                  menu={{
                    items: functionMenuItems,
                    onClick: handleFunctionSelect
                  }}
                >
                  <Button>
                    Add Function <DownOutlined />
                  </Button>
                </Dropdown>
                <Dropdown 
                  menu={{
                    items: operatorMenuItems,
                    onClick: handleOperatorSelect
                  }}
                >
                  <Button>
                    Add Operator <DownOutlined />
                  </Button>
                </Dropdown>
              </Button.Group>
            </div>
            
            <Form.Item
              name="formula"
              noStyle
              rules={[{ required: true, message: 'Please enter a formula' }]}
            >
              <TextArea
                ref={textAreaRef}
                rows={4}
                value={formulaText}
                onChange={handleTextAreaChange}
                onClick={handleTextAreaClick}
                onKeyUp={handleTextAreaKeyUp}
                placeholder="Create your formula by selecting components, functions and operators"
              />
            </Form.Item>
            
            <div style={{ marginTop: '10px' }}>
              <Button 
                type="primary" 
                onClick={checkSyntax}
              >
                Check Syntax
              </Button>
            </div>
          </Form.Item>
          
          <Form.Item
            name="isActive"
            valuePropName="checked"
          >
            <Checkbox>Mark this as Active</Checkbox>
          </Form.Item>
        </>
      )}

      {calculationType === 'flatAmount' && (
        <Form.Item
          name="amount"
          label="Amount"
          rules={[{ required: true, message: 'Please enter an amount' }]}
        >
          <Input type="number" placeholder="Enter amount" />
        </Form.Item>
      )}
    </>
  );
};

export default FormulaBuilder;